import { getGleap, isGleapReady } from '@/integrations/gleap'
import { logger } from '@/lib/logger'

import type {
  AnalyticsProvider,
  EventProperties,
  UserProperties,
} from '../types'

interface GleapUserData {
  email?: string
  name?: string
  customData?: Record<string, unknown>
}

const mapToGleapUserData = (properties: UserProperties): GleapUserData => {
  const result: GleapUserData = {}
  const customData: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined) continue
    if (key === 'language') continue
    if (key === 'email') result.email = String(value)
    else if (key === 'name') result.name = String(value)
    else customData[key] = value
  }

  if (Object.keys(customData).length > 0) {
    result.customData = customData
  }

  return result
}

// The SDK reference is fetched on each call via `getGleap()` — that
// returns the cached module once `initGleap()` has resolved, or `null`
// otherwise. Each method short-circuits on a missing reference so calls
// fired before init completes fail silently (same contract as before;
// the only change is the import is no longer at module scope).
export class GleapProvider implements AnalyticsProvider {
  name = 'gleap'
  private currentUserId: string | null = null

  track(eventName: string, properties?: EventProperties): void {
    if (!isGleapReady()) return
    const gleap = getGleap()
    if (!gleap) return
    try {
      gleap.trackEvent(eventName, properties)
    } catch (error) {
      logger.error('Gleap track failed', { eventName, error })
    }
  }

  identify(userId: string): void {
    if (!isGleapReady()) return
    const gleap = getGleap()
    if (!gleap) return
    try {
      this.currentUserId = userId
      gleap.identify(userId, {})
    } catch (error) {
      logger.error('Gleap identify failed', { userId, error })
    }
  }

  alias(newId: string, _previousId?: string): void {
    if (!isGleapReady()) return
    const gleap = getGleap()
    if (!gleap) return
    try {
      this.currentUserId = newId
      gleap.identify(newId, {})
    } catch (error) {
      logger.error('Gleap alias failed', { newId, error })
    }
  }

  setUserProperties(properties: UserProperties): void {
    if (!isGleapReady()) return
    if (!this.currentUserId) return
    const gleap = getGleap()
    if (!gleap) return
    try {
      const userData = mapToGleapUserData(properties)
      gleap.identify(this.currentUserId, userData)
    } catch (error) {
      logger.error('Gleap setUserProperties failed', { error })
    }
  }

  reset(): void {
    if (!isGleapReady()) return
    const gleap = getGleap()
    if (!gleap) return
    try {
      this.currentUserId = null
      gleap.clearIdentity()
    } catch (error) {
      logger.error('Gleap reset failed', { error })
    }
  }
}
